import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { HeaderMenuItem } from './types';
import { fitEcommerceMenuVisibleCount } from './fitEcommerceMenuVisibleCount';
import SectionMenuIcon from 'frontend/shared/SectionMenuIcon';

interface EcommerceMenuRowProps {
  items: HeaderMenuItem[];
  onMoreClick: () => void;
  /** Caps visible items before "Más...". Items that do not fit the row are omitted too. */
  maxVisible?: number;
  /** Horizontal gap included in the fit measurement. */
  itemGap?: number;
  moreIcon?: 'grid' | 'megaphone';
}

function MoreIcon({ kind }: { kind: 'grid' | 'megaphone' }) {
  if (kind === 'megaphone') {
    return (
      <svg
        aria-hidden='true'
        fill='none'
        stroke='currentColor'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='1.8'
        viewBox='0 0 24 24'
      >
        <path d='M3 10v4a1 1 0 0 0 1 1h2.5L13 19V5L6.5 9H4a1 1 0 0 0-1 1Z' />
        <path d='M16.5 9.5a3.5 3.5 0 0 1 0 5' />
        <path d='M19 7a7 7 0 0 1 0 10' />
      </svg>
    );
  }
  return <>▤</>;
}

function MenuItemLink({ item }: { item: HeaderMenuItem }) {
  return (
    <a
      className='header-ecommerce-menu-row__link'
      href={item.url || '#'}
      target={item.target || '_self'}
    >
      {item.iconSrc || item.icon ? (
        <SectionMenuIcon
          className='header-ecommerce-menu-row__glyph'
          codigo={item.icon}
          src={item.iconSrc}
        />
      ) : null}
      <span>{item.label}</span>
    </a>
  );
}

/**
 * Ecommerce inline menu bar (prototipo v18): section icons + overflow "Más..."
 * that opens the full hamburger drawer.
 */
const EcommerceMenuRow: React.FC<EcommerceMenuRowProps> = ({
  items,
  onMoreClick,
  maxVisible,
  itemGap = 0,
  moreIcon = 'grid',
}) => {
  const rowRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [fitTick, setFitTick] = useState(0);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    const moreBtn = moreRef.current;
    if (!row || !measure || !moreBtn || items.length === 0) {
      setVisibleCount(items.length);
      return;
    }

    const widths = Array.from(measure.children).map((child) => (child as HTMLElement).offsetWidth);
    const wasHidden = moreBtn.hidden;
    moreBtn.hidden = false;
    const moreWidth = moreBtn.offsetWidth;
    moreBtn.hidden = wasHidden;
    const rowStyle = getComputedStyle(row);
    const paddingLeft = parseFloat(rowStyle.paddingLeft);
    const paddingRight = parseFloat(rowStyle.paddingRight);
    const horizontalPadding =
      (Number.isFinite(paddingLeft) ? paddingLeft : 0) +
      (Number.isFinite(paddingRight) ? paddingRight : 0);
    const availableWidth = Math.max(0, row.clientWidth - horizontalPadding);
    setVisibleCount(
      fitEcommerceMenuVisibleCount(
        widths,
        availableWidth,
        moreWidth,
        maxVisible ?? widths.length,
        itemGap,
      ),
    );
  }, [itemGap, items, fitTick, maxVisible]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      setFitTick((tick) => tick + 1);
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  const visibleItems = items.slice(0, visibleCount);
  const showMore = visibleCount < items.length;

  return (
    <nav aria-label='Menú principal' className='header-ecommerce-menu-row' ref={rowRef}>
      {/* Off-screen clone used to measure natural item widths before clipping. */}
      <div aria-hidden='true' className='header-ecommerce-menu-row__measure' ref={measureRef}>
        {items.map((item) => (
          <MenuItemLink item={item} key={`measure-${item.id}`} />
        ))}
      </div>

      <div className='header-ecommerce-menu-row__items'>
        {visibleItems.map((item) => (
          <MenuItemLink item={item} key={item.id} />
        ))}
      </div>

      <button
        className='header-ecommerce-menu-row__more'
        hidden={!showMore}
        onClick={onMoreClick}
        ref={moreRef}
        type='button'
      >
        <span aria-hidden='true' className='menu-list-icon'>
          <MoreIcon kind={moreIcon} />
        </span>
        <span>Más...</span>
      </button>
    </nav>
  );
};

export default EcommerceMenuRow;
